import React, { useState } from 'react';

/**
 * HeroSection — Premium hero with clean typography,
 * accent line, stats, and scrolling ticker.
 */
const TICKER_COINS = [
    { symbol: 'BTC', color: '#f7931a' },
    { symbol: 'ETH', color: '#627eea' },
    { symbol: 'SOL', color: '#14f195' },
    { symbol: 'PEPE', color: '#4ca843' },
    { symbol: 'WIF', color: '#e8598b' },
    { symbol: 'BONK', color: '#f89b2a' },
];

const STATS = [
    { value: '3', label: 'Markets' },
    { value: '60s', label: 'Per Round' },
    { value: '24/7', label: 'Always On' },
    { value: '3%', label: 'Fee' },
];

const HeroSection = () => {
    const [notifyEmail, setNotifyEmail] = useState('');
    const [notifyStatus, setNotifyStatus] = useState(''); // '', 'sending', 'sent'

    const handleNotify = async (e) => {
        e.preventDefault();
        if (!notifyEmail) return;
        setNotifyStatus('sending');
        try {
            await fetch('/api/waitlist', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: notifyEmail }),
            });
        } catch (_) { /* network error — still show success */ }
        setNotifyStatus('sent');
    };

    return (
    <section className="relative min-h-[75vh] flex flex-col items-center justify-center px-6 pt-24 pb-10">
        {/* Ambient orbs */}
        <div className="orb orb-blue" />
        <div className="orb orb-purple" />
        <div className="orb orb-green" />
        <div className="grid-pattern" />

        {/* Content */}
        <div className="relative z-10 text-center max-w-5xl mx-auto">
            {/* Coming Soon Banner */}
            <div className="animate-in animate-in-d1 mb-6">
                <span className="inline-block px-6 py-2 rounded-full text-lg md:text-xl font-black uppercase tracking-[0.2em] text-transparent bg-clip-text"
                    style={{
                        backgroundImage: 'linear-gradient(135deg, #00e87b, #00c9ff, #8b5cf6)',
                        filter: 'drop-shadow(0 0 20px rgba(0, 232, 123, 0.3))',
                    }}>
                    Coming Soon
                </span>
            </div>

            {/* Badge */}
            <div className="animate-in animate-in-d1">
                <span className="badge-shimmer inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-blue-500/15 text-blue-400 text-[10px] uppercase tracking-[0.3em] font-bold mb-10">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    Live Prediction Market
                </span>
            </div>

            {/* Headline */}
            <h1 className="animate-reveal animate-in-d2 text-5xl md:text-7xl lg:text-[5.5rem] font-black tracking-[-0.04em] leading-[0.95] mb-4">
                <span className="gradient-text">Trade the</span>
                <br />
                <span className="gradient-text-accent">Minute.</span>
            </h1>

            {/* Accent line */}
            <div className="headline-line w-48 mx-auto mb-8" />

            {/* Subheading */}
            <p className="animate-in animate-in-d3 text-base md:text-lg text-slate-500 font-light tracking-wide max-w-xl mx-auto mb-8 leading-relaxed">
                Predict crypto price direction every 60 seconds.
                <br className="hidden md:block" />
                Three markets. Real-time odds. Instant settlement.
            </p>

            {/* Email Notification Signup */}
            <div className="animate-in animate-in-d3 max-w-md mx-auto mb-14">
                {notifyStatus === 'sent' ? (
                    <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-emerald-500/20 bg-emerald-500/5">
                        <span className="text-emerald-400 text-sm font-medium">🎉 You're on the list! We'll notify you at launch.</span>
                    </div>
                ) : (
                    <form onSubmit={handleNotify} className="flex gap-2">
                        <input
                            type="email"
                            value={notifyEmail}
                            onChange={(e) => setNotifyEmail(e.target.value)}
                            placeholder="Enter your email for launch updates"
                            required
                            className="flex-1 bg-white/[0.03] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 outline-none focus:border-blue-500/50 focus:bg-white/[0.05] transition-all font-mono"
                            id="notify-email"
                        />
                        <button
                            type="submit"
                            disabled={notifyStatus === 'sending'}
                            className="px-5 py-3 rounded-lg text-xs uppercase tracking-[0.15em] font-bold transition-all bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-500 hover:to-emerald-400 text-white shadow-[0_0_20px_rgba(0,232,123,0.15)] whitespace-nowrap"
                            id="notify-btn"
                        >
                            {notifyStatus === 'sending' ? '...' : 'Notify Me'}
                        </button>
                    </form>
                )}
                <p className="text-[10px] text-slate-700 mt-2 tracking-wide">No spam. We'll only email you when we launch.</p>
            </div>

            {/* Stats */}
            <div className="animate-in animate-in-d4 flex flex-wrap justify-center gap-px mb-14">
                {STATS.map(({ value, label }, i) => (
                    <div
                        key={label}
                        className={`flex flex-col items-center px-8 py-4 ${i < STATS.length - 1 ? 'border-r border-white/[0.04]' : ''
                            }`}
                    >
                        <span className="text-2xl md:text-3xl font-mono font-black text-white tracking-[-0.02em]">
                            {value}
                        </span>
                        <span className="text-[9px] uppercase tracking-[0.25em] text-slate-600 mt-1.5 font-semibold">
                            {label}
                        </span>
                    </div>
                ))}
            </div>
        </div>

        {/* Ticker */}
        <div className="animate-in animate-in-d5 w-full overflow-hidden relative z-10">
            <div className="landing-divider mb-5" />
            <div className="ticker-track">
                {[...TICKER_COINS, ...TICKER_COINS].map((coin, i) => (
                    <div key={i} className="flex items-center gap-2.5 px-10 shrink-0">
                        <div
                            className="w-1.5 h-1.5 rounded-full"
                            style={{ backgroundColor: coin.color, boxShadow: `0 0 8px ${coin.color}40` }}
                        />
                        <span className="text-[11px] font-mono tracking-[0.15em] text-slate-600 font-medium">
                            {coin.symbol}
                        </span>
                    </div>
                ))}
            </div>
            <div className="landing-divider mt-5" />
        </div>
    </section>
    );
};

export default HeroSection;
